import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { fetchStepStatus } from '../../lib/onboarding-funnel-api.js';

export function StepDropOffPanel({ businessTypeFilter }: { businessTypeFilter: string | 'all' }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['onboarding-funnel-step-status'],
    queryFn: fetchStepStatus,
  });

  if (isLoading) return <Skeleton data-testid="drop-off-skeleton" />;
  if (isError) {
    return (
      <ErrorState description="Couldn't load step drop-off data." onRetry={() => void refetch()} />
    );
  }

  const rows = data?.steps ?? [];
  if (rows.length === 0) return <EmptyState title="No step data yet." />;

  // Percentage denominator, locked (spec §4): count at this step's status /
  // total tenants who have REACHED this step -- not total tenants overall.
  const byStep = new Map<string, { total: number; byStatus: Map<string, number> }>();
  for (const row of rows) {
    const entry = byStep.get(row.stepKey) ?? { total: 0, byStatus: new Map<string, number>() };
    entry.total += row.count;
    entry.byStatus.set(row.status, (entry.byStatus.get(row.status) ?? 0) + row.count);
    byStep.set(row.stepKey, entry);
  }

  return (
    <div className="flex flex-col gap-4">
      {businessTypeFilter !== 'all' && (
        <p className="text-body-sm text-muted">
          Note: step drop-off counts are global across all business types (per-type breakdown isn't
          stored yet).
        </p>
      )}
      <Table aria-label="Step drop-off">
        <TableHead>
          <TableRow>
            <TableHeaderCell>Step</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Count</TableHeaderCell>
            <TableHeaderCell>% of tenants reaching this step</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...byStep.entries()].flatMap(([stepKey, entry]) =>
            [...entry.byStatus.entries()].map(([status, count]) => (
              <TableRow key={`${stepKey}-${status}`}>
                <TableCell>{stepKey}</TableCell>
                <TableCell>{status}</TableCell>
                <TableCell>{count}</TableCell>
                <TableCell>{Math.round((count / entry.total) * 100)}%</TableCell>
              </TableRow>
            )),
          )}
        </TableBody>
      </Table>
    </div>
  );
}
